import React, {
  ReactNode,
  useState,
  useContext,
  createContext,
  useCallback
} from 'react';
import { ITripFolder } from '../../Models/trips/trip-folder.model';
import { ITripActivity } from '../../Models/trips/trip-activity.model';
import { ITripDestination } from '../../Models/trips/trip-destination.model';
import { useTripData } from './trip-data-context';

export interface TripSelectionContextType {
  // PHASE 5: Clean interface with consistent naming
  // Single selections (null = nothing selected)
  selectedFolderId: string | null;
  setSelectedFolderId: (folderId: string | null) => void;
  selectedDestinationId: string | null;
  setSelectedDestinationId: (destinationId: string | null) => void;
  selectedActivityId: string | null;
  setSelectedActivityId: (activityId: string | null) => void;

  // Multi-selections (arrays = multiple items selected)
  activeFolderIds: string[];
  setActiveFolderIds: (folderIds: string[]) => void;
  visibleDayIds: Set<string>;
  setVisibleDayIds: (dayIds: Set<string>) => void;

  // Helper functions (compute from trip data + selection)
  getSelectedDestination: () => ITripDestination | null;
  getSelectedActivity: () => ITripActivity | null;
  getSelectedFolder: () => ITripFolder | null;
}

const TripSelectionContext = createContext<
  TripSelectionContextType | undefined
>(undefined);

export const TripSelectionProvider: React.FC<{ children: ReactNode }> = ({
  children
}) => {
  const { trip } = useTripData();

  // Pure UI selection state with clean naming
  const [selectedFolderId, setSelectedFolderIdState] = useState<string | null>(
    null
  );
  const [activeFolderIds, setActiveFolderIdsState] = useState<string[]>([]);
  const [selectedDestinationId, setSelectedDestinationIdState] = useState<
    string | null
  >(null);
  const [selectedActivityId, setSelectedActivityIdState] = useState<
    string | null
  >(null);
  const [visibleDayIds, setVisibleDayIdsState] = useState<Set<string>>(
    new Set()
  );

  //  Helper functions to lookup selected entities from trip data
  const getSelectedDestination = useCallback((): ITripDestination | null => {
    if (!selectedDestinationId || !trip?.destinations) return null;
    return (
      trip.destinations.find(
        (d) =>
          d.googlePlacesId === selectedDestinationId ||
          d.name === selectedDestinationId
      ) ?? null
    );
  }, [selectedDestinationId, trip?.destinations]);

  const getSelectedActivity = useCallback((): ITripActivity | null => {
    if (!selectedActivityId || !trip?.activities) return null;
    return trip.activities.find((a) => a.id === selectedActivityId) ?? null;
  }, [selectedActivityId, trip?.activities]);

  const getSelectedFolder = useCallback((): ITripFolder | null => {
    if (!selectedFolderId || !trip?.folders) return null;
    return (
      (trip.folders.find(
        (f) => f.id === selectedFolderId
      ) as unknown as ITripFolder) ?? null
    );
  }, [selectedFolderId, trip?.folders]);

  // Enhanced setSelectedFolderId that automatically updates folder-related state
  const setSelectedFolderId = useCallback((folderId: string | null) => {
    setSelectedFolderIdState(folderId);
  }, []);

  // Wrapper for setSelectedActivityId that also updates activity object (for backward compat)
  const setSelectedActivityId = useCallback((activityId: string | null) => {
    setSelectedActivityIdState(activityId);
  }, []);

  const value: TripSelectionContextType = {
    //  New clean interface
    selectedFolderId,
    setSelectedFolderId,
    selectedDestinationId,
    setSelectedDestinationId: setSelectedDestinationIdState,
    selectedActivityId,
    setSelectedActivityId,
    activeFolderIds,
    setActiveFolderIds: setActiveFolderIdsState,
    visibleDayIds,
    setVisibleDayIds: setVisibleDayIdsState,
    getSelectedDestination,
    getSelectedActivity,
    getSelectedFolder
  };

  return (
    <TripSelectionContext.Provider value={value}>
      {children}
    </TripSelectionContext.Provider>
  );
};

export const useTripSelection = () => {
  const context = useContext(TripSelectionContext);
  if (context === undefined) {
    throw new Error(
      'useTripSelection must be used within a TripSelectionProvider'
    );
  }
  return context;
};
